/**
 * The prototype's own path resolver and navigation model.
 *
 * The TanStack tree owns two entries (`/styleguide/verification` and its splat);
 * everything below is resolved here. That keeps the prototype's eighteen pages out of
 * `routes/tree.ts`, where they would read as product IA, while every page stays
 * deep-linkable — which matters because the point of the prototype is to be
 * reviewed, and a review comment needs a URL.
 *
 * ── The navigation IS the argument ──
 *
 * `NAV` below is the substantive design proposal in this file. It groups the
 * product's eighteen surfaces under five verbs — DECIDE, DEFINE, EVIDENCE, PROVE,
 * OPERATE — which are the phases of the verification loop in the order they
 * happen, not a list of the modules that exist.
 *
 * That ordering is a deliberate correction to what the UX audits found: the
 * shipped console "prioritizes queue metrics and historical runs" and reads as
 * "an internal ops dashboard rather than an integrator's front door"
 * (`VERIFICATION-UX-INTEGRATION-AUDIT.md`). Grouping by loop phase means a new
 * user reads the rail top-to-bottom and learns the product's causality: you
 * DEFINE what must be true, EVIDENCE collects what happened, the engine runs, you
 * DECIDE the exceptions, and PROVE is what you hand an auditor. DECIDE sits at the
 * top rather than in loop order because it is the daily job — the rail is read by
 * a reviewer twenty times a day and by a new integrator once.
 */

import type { IconName } from "./ui";
import {
  BLOCKED_POLICIES,
  CANDIDATES_NEEDING_ORACLE,
  MY_QUEUE,
  OPEN_FINDINGS,
  UNHEALTHY_SOURCES,
} from "./data";

/** The prototype's mount point. Every internal href is built from this. */
export const BASE = "/styleguide/verification";

/** The standalone HTML explainer, served from `excellent/public/styleguide/`. */
export const GUIDE_DOC_URL = "/styleguide/verification-handbook.html";

export type PageId =
  | "overview"
  | "queue"
  | "finding"
  | "findings"
  | "runs"
  | "run"
  | "workflows"
  | "workflow"
  | "controls"
  | "control"
  | "policies"
  | "policy"
  | "evidence"
  | "artifact"
  | "connectors"
  | "receipts"
  | "receipt"
  | "packs"
  | "pack"
  | "audit-room"
  | "workers"
  | "scorecard"
  | "runtime"
  | "api"
  | "workflow-new"
  | "trust"
  | "posture"
  | "buyable"
  | "guide"
  | "design"
  | "not-found";

export interface Route {
  page: PageId;
  /** The object id for a detail page. */
  id?: string;
  /** The raw remainder, for the 404 message. */
  raw: string;
}

/**
 * Resolve the splat remainder to a page.
 *
 * A two-segment path is always `<list>/<id>`, so detail pages need no per-page
 * parsing. Unknown paths resolve to `not-found` rather than silently falling back
 * to the overview: a prototype that answers every URL with its home page hides
 * broken links from the very review it exists to receive.
 */
export function resolveRoute(splat: string | undefined): Route {
  const raw = (splat ?? "").replace(/^\/+|\/+$/g, "");
  if (raw === "") return { page: "overview", raw };

  // `?? ""` because `noUncheckedIndexedAccess` types a destructured array element
  // as possibly-undefined even when the split cannot produce one — `raw` is
  // non-empty by the guard above, so `head` always has a value.
  const parts = raw.split("/");
  const head = parts[0] ?? "";
  const id = parts[1];

  // `workflows/new` is the intake surface, not a detail page for an object
  // whose id happens to be "new". Checked before the generic two-segment rule
  // because that rule would otherwise route it to `workflow` and 404 on lookup.
  if (raw === "workflows/new") return { page: "workflow-new", raw };

  if (id) {
    const detail: Record<string, PageId> = {
      queue: "finding",
      findings: "finding",
      runs: "run",
      workflows: "workflow",
      controls: "control",
      policies: "policy",
      evidence: "artifact",
      receipts: "receipt",
      packs: "pack",
    };
    const page = detail[head];
    return page ? { page, id, raw } : { page: "not-found", raw };
  }

  const list: Record<string, PageId> = {
    queue: "queue",
    findings: "findings",
    runs: "runs",
    workflows: "workflows",
    controls: "controls",
    policies: "policies",
    evidence: "evidence",
    connectors: "connectors",
    receipts: "receipts",
    packs: "packs",
    "audit-room": "audit-room",
    workers: "workers",
    scorecard: "scorecard",
    runtime: "runtime",
    api: "api",
    trust: "trust",
    posture: "posture",
    buyable: "buyable",
    guide: "guide",
    design: "design",
  };
  const page = list[head];
  return page ? { page, raw } : { page: "not-found", raw };
}

/** Build an in-prototype href. `href()` is the index. */
export function href(...segments: string[]): string {
  return segments.length === 0 ? BASE : `${BASE}/${segments.join("/")}`;
}

/* ── Navigation ──────────────────────────────────────────────────────────── */

export interface NavItem {
  page: PageId;
  path: string;
  label: string;
  icon: IconName;
  /** One-line purpose — shown as the nav tooltip and on the guide's IA map. */
  purpose: string;
  count?: number;
  countTone?: "fail" | "unknown" | "accent";
  /** Detail pages that should light this rail row up. */
  alsoActiveFor?: PageId[];
}

export interface NavGroup {
  label: string | null;
  /** Why this group exists, for the guide's IA table. */
  rationale: string;
  items: NavItem[];
}

export const NAV: NavGroup[] = [
  {
    label: null,
    rationale: "The one screen that answers 'could we hand an auditor a pack today?'",
    items: [
      {
        page: "overview",
        path: "",
        label: "Overview",
        icon: "gauge",
        purpose: "Trust posture for the period: control verdicts, what blocks a pack, what changed today.",
      },
    ],
  },
  {
    label: "Decide",
    rationale:
      "The reviewer's daily job. First in the rail because it is opened many times a day, whereas Define is opened once at setup.",
    items: [
      {
        page: "queue",
        path: "queue",
        label: "Decision inbox",
        icon: "inbox",
        purpose: "Findings waiting on a decision from you, worst-first. The default landing place for a reviewer.",
        count: MY_QUEUE.length,
        countTone: "accent",
      },
      {
        page: "findings",
        path: "findings",
        label: "Findings",
        icon: "flag",
        purpose: "Every FAIL and UNKNOWN across the tenant, filterable by control, owner, severity and age.",
        count: OPEN_FINDINGS.length,
        countTone: "fail",
        // Detail pages hang off THIS row, not the queue's: every finding link in
        // the product is `href("findings", id)`, so attaching them to the queue
        // meant opening a finding from the backlog highlighted a different rail
        // row and gave you a breadcrumb that navigated somewhere you had not been.
        alsoActiveFor: ["finding"],
      },
      {
        page: "runs",
        path: "runs",
        label: "Runs",
        icon: "play",
        purpose: "Verification run history. A run detail page is the full story: claim, checks, evidence, receipt, decisions.",
        alsoActiveFor: ["run"],
      },
    ],
  },
  {
    label: "Define",
    rationale: "What must be true, and how the engine is allowed to decide it. Set up once, revised deliberately.",
    items: [
      {
        page: "workflows",
        path: "workflows",
        label: "Workflows",
        icon: "link",
        purpose:
          "Connect a real workflow — human or agent — by naming an independent oracle. Nothing is admitted without one.",
        count: CANDIDATES_NEEDING_ORACLE.length,
        countTone: "unknown",
        alsoActiveFor: ["workflow", "workflow-new"],
      },
      {
        page: "controls",
        path: "controls",
        label: "Controls",
        icon: "shield",
        purpose: "The control library and its requirements: owner, frequency, population, current verdict.",
        alsoActiveFor: ["control"],
      },
      {
        page: "policies",
        path: "policies",
        label: "Policies",
        icon: "file",
        purpose:
          "The checkable logic behind each control, versioned. Loosening a policy needs impact analysis and a second human.",
        count: BLOCKED_POLICIES.length,
        countTone: "unknown",
        alsoActiveFor: ["policy"],
      },
    ],
  },
  {
    label: "Evidence",
    rationale: "Where the facts come from. Separated from Define because sources fail on their own schedule.",
    items: [
      {
        page: "evidence",
        path: "evidence",
        label: "Evidence library",
        icon: "book",
        purpose: "Content-addressed artifacts and the observations derived from them, with full lineage.",
        alsoActiveFor: ["artifact"],
      },
      {
        page: "connectors",
        path: "connectors",
        label: "Connectors",
        icon: "plug",
        purpose: "Read-only source connections, their scopes, cadence and freshness. A stale source is why a control is UNKNOWN.",
        count: UNHEALTHY_SOURCES.length,
        countTone: "fail",
      },
    ],
  },
  {
    label: "Prove",
    rationale: "The output the customer actually buys: something an outside party can check without trusting us.",
    items: [
      {
        page: "receipts",
        path: "receipts",
        label: "Receipts",
        icon: "seal",
        purpose: "The append-only ledger binding each verdict to its evidence, policy version and signing key.",
        alsoActiveFor: ["receipt"],
      },
      {
        page: "packs",
        path: "packs",
        label: "Evidence packs",
        icon: "package",
        purpose: "Scoped, tamper-evident exports that replay offline. Carries its scope limitations in the open.",
        alsoActiveFor: ["pack"],
      },
      {
        page: "audit-room",
        path: "audit-room",
        label: "Audit room",
        icon: "door",
        purpose: "Time-limited read-only access for an external reviewer, with every view and download logged.",
      },
    ],
  },
  {
    label: "Operate",
    rationale: "The machinery's own health. Read-only for a reviewer; the operator's home.",
    items: [
      {
        page: "workers",
        path: "workers",
        label: "Semantic workers",
        icon: "cpu",
        purpose: "Model-based checkers, their calibration and boundary. A model may never issue an authoritative PASS.",
      },
      {
        page: "scorecard",
        path: "scorecard",
        label: "Scorecard",
        icon: "chart",
        purpose: "The nine metrics that decide whether this workspace's verification is trustworthy enough to sell on.",
      },
      {
        page: "runtime",
        path: "runtime",
        label: "Runtime & keys",
        icon: "server",
        purpose: "Where evidence lives, who holds the signing key, and whether offline replay works.",
      },
      {
        page: "api",
        path: "api",
        label: "API & SDK",
        icon: "code",
        purpose:
          "The served endpoints, their scopes, and the one operation deliberately withheld from them.",
      },
      {
        page: "trust",
        path: "trust",
        label: "Trust root",
        icon: "shield",
        purpose:
          "Out-of-band keys and chain anchoring: what an outside party can check without trusting us, and where that still has a ceiling.",
      },
      {
        page: "posture",
        path: "posture",
        label: "Security posture",
        icon: "lock",
        purpose:
          "The enterprise security questionnaire, answered in the product. Unanswered rows are shown as unanswered.",
      },
    ],
  },
  {
    label: "Reference",
    rationale: "Documentation, in the product rather than beside it.",
    items: [
      {
        page: "guide",
        path: "guide",
        label: "How it works",
        icon: "book",
        purpose: "The product explained end to end: the loop, the vocabulary, the jobs, what is built and what is not.",
      },
      {
        page: "buyable",
        path: "buyable",
        label: "Is it buyable?",
        icon: "check",
        purpose:
          "The five things a stranger must be able to do, each with the gate that protects it and the ceiling on its claim.",
      },
      {
        page: "design",
        path: "design",
        label: "Design system",
        icon: "code",
        purpose: "The prototype's own tokens, primitives and rules — the spec if this proposal is accepted.",
      },
    ],
  },
];

/** Flat list, for lookups and for the guide's page inventory. */
export const NAV_ITEMS: NavItem[] = NAV.flatMap((g) => g.items);

/** Which rail row should read as active for a resolved page. */
export function activeNavPage(page: PageId): PageId | null {
  for (const item of NAV_ITEMS) {
    if (item.page === page) return item.page;
    if (item.alsoActiveFor?.includes(page)) return item.page;
  }
  return null;
}

/** The rail row for a page, so breadcrumbs can name its section. */
export function navItemFor(page: PageId): NavItem | undefined {
  const active = activeNavPage(page);
  return NAV_ITEMS.find((i) => i.page === active);
}

/** The group label a page sits under. */
export function groupFor(page: PageId): string | null {
  const active = activeNavPage(page);
  for (const g of NAV) {
    if (g.items.some((i) => i.page === active)) return g.label;
  }
  return null;
}
